import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, FileText, Trash2, Copy, CheckCircle2, Clock, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Assessment } from "@shared/schema";
import { PreparationChecklistSheet } from "@/components/workflow/common/PreparationChecklistSheet";

// Computed once at module load — not inside the component — so they never recompute on re-render
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 10 }, (_, i) => CURRENT_YEAR - i);

export default function AssessmentsList() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isChecklistOpen, setIsChecklistOpen] = useState(false);
  const [assessmentToDelete, setAssessmentToDelete] = useState<Assessment | null>(null);
  const [activeTab, setActiveTab] = useState<"all" | "in_progress" | "completed">("all");

  // Form state
  const [name, setName] = useState("");
  const [baselineYear, setBaselineYear] = useState<number>(CURRENT_YEAR);
  const [periodType, setPeriodType] = useState<'annually' | 'semesterly' | 'quarterly' | ''>('');
  const [subPeriod, setSubPeriod] = useState<string>('');
  const [cloneFromLatest, setCloneFromLatest] = useState(false);

  const computedPeriod = (() => {
    if (!periodType) return null;
    if ((periodType === 'semesterly' || periodType === 'quarterly') && !subPeriod) return null;

    const pad = (n: number) => String(n).padStart(2, '0');
    const fmt = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;

    const ranges: Record<string, [number, number, number, number]> = {
      annually: [1, 1, 12, 31],
      'semesterly-H1': [1, 1, 6, 30],
      'semesterly-H2': [7, 1, 12, 31],
      'quarterly-Q1': [1, 1, 3, 31],
      'quarterly-Q2': [4, 1, 6, 30],
      'quarterly-Q3': [7, 1, 9, 30],
      'quarterly-Q4': [10, 1, 12, 31],
    };

    const key = periodType === 'annually' ? 'annually' : `${periodType}-${subPeriod}`;
    const range = ranges[key];
    if (!range) return null;

    const [sm, sd, em, ed] = range;
    return {
      periodStart: fmt(baselineYear, sm, sd),
      periodEnd: fmt(baselineYear, em, ed),
      startDate: new Date(baselineYear, sm - 1, sd),  // local midnight for display
      endDate: new Date(baselineYear, em - 1, ed),
    };
  })();

  // Fetch assessments
  const { data: assessments, isLoading } = useQuery<Assessment[]>({
    queryKey: ["assessments"],
    queryFn: async () => {
      const res = await fetch("/api/assessments", { credentials: "include" });
      if (!res.ok) {
        throw new Error("Failed to fetch assessments");
      }
      return res.json();
    },
  });

  // Create assessment mutation
  const createMutation = useMutation({
    mutationFn: async (data: {
      name: string;
      periodStart: string;
      periodEnd: string;
      cloneFromLatest: boolean;
    }) => {
      const res = await fetch("/api/assessments", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Failed to create assessment");
      }

      return res.json();
    },
    onSuccess: (newAssessment) => {
      queryClient.invalidateQueries({ queryKey: ["assessments"] });
      setIsCreateDialogOpen(false);
      resetForm();
      toast({
        title: "Assessment Created",
        description: `${newAssessment.name} has been created successfully.`,
      });
      // Navigate to the new assessment's workflow
      setLocation(`/workflow/${newAssessment.id}`);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete assessment mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/assessments/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Failed to delete assessment");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assessments"] });
      setAssessmentToDelete(null);
      toast({
        title: "Assessment Deleted",
        description: "The assessment and all its data have been permanently deleted.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const canDelete = user?.role === "admin" || user?.role === "company_admin";

  const resetForm = () => {
    setName("");
    setBaselineYear(CURRENT_YEAR);
    setPeriodType('');
    setSubPeriod('');
    setCloneFromLatest(false);
  };

  const handleCreate = () => {
    if (!name.trim()) {
      toast({
        title: "Validation Error",
        description: "Assessment name is required",
        variant: "destructive",
      });
      return;
    }

    if (!periodType) {
      toast({
        title: "Validation Error",
        description: "Please select a period type",
        variant: "destructive",
      });
      return;
    }

    if ((periodType === 'semesterly' || periodType === 'quarterly') && !subPeriod) {
      toast({
        title: "Validation Error",
        description: "Please select a sub-period",
        variant: "destructive",
      });
      return;
    }

    // computedPeriod is guaranteed non-null here: validation above already
    // checked periodType is set and subPeriod is set when required.
    if (!computedPeriod) return;

    createMutation.mutate({
      name,
      periodStart: computedPeriod.periodStart,
      periodEnd: computedPeriod.periodEnd,
      cloneFromLatest,
    });
  };

  const getAssessmentStatus = (assessment: Assessment) => {
    if (assessment.status === "finalized") return "finalized" as const;
    if (assessment.locked || assessment.status === "complete") return "completed" as const;
    if ((assessment.completenessPercent ?? 0) > 0) return "in_progress" as const;
    return "not_started" as const;
  };

  const getStatusBadge = (assessment: Assessment) => {
    const status = getAssessmentStatus(assessment);
    switch (status) {
      case "finalized":
        return (
          <span className="px-2.5 py-1 bg-green-100 rounded-full text-green-700 text-xs font-bold">
            {"Finalized"}
          </span>
        );
      case "completed":
        return (
          <span className="px-2.5 py-1 bg-blue-100 rounded-full text-blue-700 text-xs font-bold">
            {"Complete"}
          </span>
        );
      case "in_progress":
        return (
          <span className="px-2.5 py-1 bg-amber-50 rounded-full text-amber-600 text-xs font-bold">
            {"In Progress"}
          </span>
        );
      default:
        return (
          <span className="px-2.5 py-1 bg-slate-100 rounded-full text-slate-500 text-xs font-bold">
            {"Not Started"}
          </span>
        );
    }
  };

  const getStatusIcon = (assessment: Assessment) => {
    const status = getAssessmentStatus(assessment);
    switch (status) {
      case "finalized":
        return (
          <div className="w-[31px] h-[31px] rounded-full bg-green-100 flex items-center justify-center">
            <Lock className="h-4 w-4 text-green-700" />
          </div>
        );
      case "completed":
        return (
          <div className="w-[31px] h-[31px] rounded-full bg-blue-100 flex items-center justify-center">
            <CheckCircle2 className="h-4 w-4 text-blue-700" />
          </div>
        );
      case "in_progress":
        return (
          <div className="w-[31px] h-[31px] rounded-full bg-blue-50 flex items-center justify-center">
            <FileText className="h-4 w-4 text-blue-600" />
          </div>
        );
      default:
        return (
          <div className="w-[31px] h-[31px] rounded-full bg-slate-100 flex items-center justify-center">
            <Clock className="h-4 w-4 text-slate-500" />
          </div>
        );
    }
  };

  const getProgressBarColor = (assessment: Assessment) => {
    const status = getAssessmentStatus(assessment);
    switch (status) {
      case "finalized": return "bg-green-500";
      case "completed": return "bg-blue-500";
      case "in_progress": return "bg-brand-accent";
      default: return "bg-slate-200";
    }
  };

  const getActionLabel = (assessment: Assessment) => {
    const status = getAssessmentStatus(assessment);
    switch (status) {
      case "finalized": return "Open";
      case "completed": return "Open";
      case "in_progress": return "Resume";
      default: return "Start";
    }
  };

  const formatPeriod = (start: Date, end: Date) => {
    const formatDate = (date: Date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
    return `${formatDate(start)} - ${formatDate(end)}`;
  };

  const filteredAssessments = assessments?.filter((a) => {
    if (activeTab === "all") return true;
    const status = getAssessmentStatus(a);
    if (activeTab === "completed") return status === "completed" || status === "finalized";
    return status === activeTab;
  });

  const tabs = [
    { key: "all" as const, label: "All" },
    { key: "in_progress" as const, label: "In Progress" },
    { key: "completed" as const, label: "Completed" },
  ];

  return (
    <div className="container mx-auto py-8 px-4">
      {/* Header */}
      <div className="flex justify-between items-end mb-8">
        <div className="flex flex-col gap-1">
          <h1 className="text-3xl font-black text-slate-900">{"Assessments"}</h1>
          <p className="text-lg text-slate-500">
            {"Manage your sustainability assessment periods"}
          </p>
        </div>

        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/80 rounded-3xl px-6 py-3 font-bold shadow-[0px_4px_6px_-4px_rgba(0,68,76,0.20),0px_10px_15px_-3px_rgba(0,68,76,0.20)]">
              <Plus className="mr-2 h-4 w-4" />
              {"Create Assessment"}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[560px] rounded-3xl p-0 gap-0 overflow-hidden">
            {/* Header */}
            <DialogHeader className="p-6 border-b border-slate-100">
              <DialogTitle className="text-xl font-bold text-brand-accent">
                {"Create New Assessment"}
              </DialogTitle>
              <DialogDescription className="text-sm text-slate-500">
                {"Create a new sustainability assessment for a specific period"}
              </DialogDescription>
            </DialogHeader>

            {/* Form Body */}
            <div className="p-6 flex flex-col gap-6">
              {/* Assessment Name */}
              <div className="flex flex-col gap-2">
                <Label htmlFor="name" className="text-sm font-semibold text-slate-700">
                  {"Assessment Name *"}
                </Label>
                <Input
                  id="name"
                  className="h-12 px-4 py-3 bg-slate-50 rounded-2xl border-slate-200"
                  placeholder={"e.g., Q1 2026, FY 2025-2026, H1 2026"}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              {/* Row 1: Baseline Year + Period Type */}
              <div className="flex gap-4">
                <div className="flex-1 flex flex-col gap-2">
                  <Label className="text-sm font-semibold text-slate-700">
                    {"Baseline Year"}
                  </Label>
                  <Select
                    value={String(baselineYear)}
                    onValueChange={(v) => setBaselineYear(Number(v))}
                  >
                    <SelectTrigger className="h-12 px-4 bg-slate-50 rounded-2xl border-slate-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {YEAR_OPTIONS.map((y) => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex-1 flex flex-col gap-2">
                  <Label className="text-sm font-semibold text-slate-700">
                    {"Period type"}
                  </Label>
                  <Select
                    value={periodType}
                    onValueChange={(v) => {
                      setPeriodType(v as 'annually' | 'semesterly' | 'quarterly');
                      setSubPeriod('');
                    }}
                  >
                    <SelectTrigger className="h-12 px-4 bg-slate-50 rounded-2xl border-slate-200">
                      <SelectValue placeholder={"Select period type"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="annually">{"Annually"}</SelectItem>
                      <SelectItem value="semesterly">{"Semesterly"}</SelectItem>
                      <SelectItem value="quarterly">{"Quarterly"}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Row 2: Sub-period (conditional) */}
              {(periodType === 'semesterly' || periodType === 'quarterly') && (
                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-semibold text-slate-700">
                    {"Sub-period"}
                  </Label>
                  <Select value={subPeriod} onValueChange={setSubPeriod}>
                    <SelectTrigger className="h-12 px-4 bg-slate-50 rounded-2xl border-slate-200">
                      <SelectValue placeholder={"Select sub-period"} />
                    </SelectTrigger>
                    <SelectContent>
                      {periodType === 'semesterly' && (
                        <>
                          <SelectItem value="H1">{"H1 (Jan - Jun)"}</SelectItem>
                          <SelectItem value="H2">{"H2 (Jul - Dec)"}</SelectItem>
                        </>
                      )}
                      {periodType === 'quarterly' && (
                        <>
                          <SelectItem value="Q1">{"Q1 (Jan - Mar)"}</SelectItem>
                          <SelectItem value="Q2">{"Q2 (Apr - Jun)"}</SelectItem>
                          <SelectItem value="Q3">{"Q3 (Jul - Sep)"}</SelectItem>
                          <SelectItem value="Q4">{"Q4 (Oct - Dec)"}</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Row 3: Computed period display (read-only) */}
              {computedPeriod && (
                <div className="flex flex-col gap-1">
                  <Label className="text-sm font-semibold text-slate-700">
                    {"Reporting Period"}
                  </Label>
                  <p className="h-12 px-4 flex items-center bg-slate-100 rounded-2xl text-sm text-slate-600 font-medium">
                    {formatPeriod(computedPeriod.startDate, computedPeriod.endDate)}
                  </p>
                </div>
              )}

              {/* Clone Toggle — only shown when a previous assessment exists to clone from */}
              {assessments && assessments.length > 0 && <div className="py-4 border-t border-b border-slate-100 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-500/10 rounded-2xl flex justify-center items-center">
                    <Copy className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold text-slate-800">
                      {"Clone data from latest assessment"}
                    </span>
                    <span className="text-xs text-slate-500">
                      {"All detailed responses, policies, and evidence will start fresh"}
                    </span>
                  </div>
                </div>
                <Switch
                  checked={cloneFromLatest}
                  onCheckedChange={setCloneFromLatest}
                  className="data-[state=checked]:bg-primary"
                />
              </div>}

              {/* Preparation checklist link */}
              <button
                type="button"
                onClick={() => setIsChecklistOpen(true)}
                className="cursor-pointer text-xs text-slate-500 hover:text-brand-accent transition-colors text-left underline underline-offset-2"
              >
                Not sure what you'll need? See the preparation checklist →
              </button>
            </div>

            {/* Footer */}
            <DialogFooter className="p-6 bg-slate-50 flex-row justify-end gap-3">
              <Button
                variant="outline"
                className="rounded-2xl px-5"
                onClick={() => {
                  setIsCreateDialogOpen(false);
                  resetForm();
                }}
              >
                {"Cancel"}
              </Button>
              <Button
                className="bg-primary hover:bg-primary/80 rounded-2xl px-6 font-semibold shadow-[0px_2px_4px_-2px_rgba(0,68,76,0.20),0px_4px_6px_-1px_rgba(0,68,76,0.20)]"
                onClick={handleCreate}
                disabled={createMutation.isPending}
              >
                {createMutation.isPending ? "Creating..." : "Create Assessment"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 flex gap-6 mb-0">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={cn(
              "px-2 pb-4 text-sm font-bold border-b-2 transition-colors",
              activeTab === tab.key
                ? "border-brand-accent text-brand-accent"
                : "border-transparent text-slate-500 hover:text-slate-700"
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : !assessments || assessments.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {"No assessments yet"}
          </h3>
          <p className="text-gray-600 mb-4">
            {"Create your first assessment to start tracking sustainability data"}
          </p>
          <Button
            className="bg-primary hover:bg-primary/80 rounded-3xl px-6 py-3 font-bold"
            onClick={() => setIsCreateDialogOpen(true)}
          >
            <Plus className="mr-2 h-4 w-4" />
            {"Create Assessment"}
          </Button>
        </div>
      ) : (
        <div className="bg-white">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="text-xs font-bold text-slate-500 uppercase tracking-wide">{"Name"}</TableHead>
                <TableHead className="text-xs font-bold text-slate-500 uppercase tracking-wide">{"Period"}</TableHead>
                <TableHead className="text-xs font-bold text-slate-500 uppercase tracking-wide">{"Status"}</TableHead>
                <TableHead className="text-xs font-bold text-slate-500 uppercase tracking-wide">{"Progress"}</TableHead>
                <TableHead className="text-xs font-bold text-slate-500 uppercase tracking-wide text-right">{"Actions"}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAssessments?.map((assessment) => (
                <TableRow
                  key={assessment.id}
                  className="cursor-pointer hover:bg-slate-50/50 border-t border-slate-100 h-20"
                  onClick={() => setLocation(`/workflow/${assessment.id}`)}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {getStatusIcon(assessment)}
                      <span className="text-base font-semibold text-slate-900">{assessment.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-slate-500">
                      {formatPeriod(assessment.periodStart, assessment.periodEnd)}
                    </span>
                  </TableCell>
                  <TableCell>{getStatusBadge(assessment)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-4 w-40 min-w-28">
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={cn("h-2 rounded-full", getProgressBarColor(assessment))}
                          style={{ width: `${assessment.completenessPercent}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-slate-700">
                        {assessment.completenessPercent}%
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        className="text-sm font-bold text-slate-500 hover:text-slate-700"
                        onClick={(e) => {
                          e.stopPropagation();
                          setLocation(`/workflow/${assessment.id}`);
                        }}
                      >
                        {getActionLabel(assessment)}
                      </button>
                      {assessment.locked && (
                        <button
                          className="text-sm font-bold text-primary hover:text-primary/80"
                          onClick={(e) => {
                            e.stopPropagation();
                            setLocation(`/report/${assessment.id}`);
                          }}
                        >
                          View Report
                        </button>
                      )}
                      {canDelete && (
                        <button
                          className="p-1.5 rounded-2xl hover:bg-slate-100 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setAssessmentToDelete(assessment);
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-slate-400" />
                        </button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!assessmentToDelete} onOpenChange={(open) => !open && setAssessmentToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{"Delete Assessment"}</AlertDialogTitle>
            <AlertDialogDescription>
              {"Are you sure you want to delete "}
              <strong>{assessmentToDelete?.name}</strong>
              {"? All data for this assessment will be permanently deleted, including all responses, reports, and uploaded documents. This action cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{"Cancel"}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => assessmentToDelete && deleteMutation.mutate(assessmentToDelete.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete Assessment"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PreparationChecklistSheet
        open={isChecklistOpen}
        onOpenChange={setIsChecklistOpen}
      />
    </div>
  );
}
